import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, FileText, Shield, Scale, BadgeCheck } from "lucide-react";

const CompliancePage = () => {
  return (
    <div className="min-h-screen bg-white page-transition" data-testid="compliance-page">
      <Helmet>
        <title>Compliance & Disclaimers | IRDAI Guidelines | FutureSanchay</title>
        <meta name="description" content="FutureSanchay compliance information, IRDAI guidelines, legal disclaimers, and privacy policy. We are authorized insurance distributors, not LIC representatives." />
        <link rel="canonical" href="https://futuresanchay.com/compliance" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" data-testid="compliance-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gray-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Compliance & Disclaimers</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
              <Scale className="w-4 h-4" />
              Legal Information
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 animate-fade-in-up delay-100">
              Compliance & Disclaimers
            </h1>
            <p className="text-lg text-gray-600 animate-fade-in-up delay-200">
              Important legal information, regulatory compliance details, and disclaimers for using our services.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-16 md:pb-24">
        <div className="container-main">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Important Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 md:p-8 animate-fade-in-up" data-testid="notice-section">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    Important Notice
                  </h2>
                  <p className="text-gray-700">
                    This website is operated by <span className="font-semibold">Wise Owl Insurance & Investments</span> (brand name: FutureSanchay), an authorized 
                    insurance distributor. We are <span className="font-semibold text-red-600">NOT</span> representatives or employees of Life Insurance 
                    Corporation of India (LIC). Our role is to provide guidance and facilitate the 
                    purchase of insurance products based on your needs.
                  </p>
                </div>
              </div>
            </div>

            {/* Disclaimer Section */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg p-6 md:p-8 animate-fade-in-up delay-100" data-testid="disclaimer-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-sky-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Disclaimers
                </h2>
              </div>
              
              <div className="space-y-6 text-gray-700">
                <div className="border-l-4 border-sky-200 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">1. Not an Offer or Solicitation</h3>
                  <p>
                    The information provided on this website does not constitute an offer or solicitation 
                    to buy any insurance product. It is for informational purposes only. The actual terms, 
                    conditions, and benefits are governed by the respective policy documents.
                  </p>
                </div>

                <div className="border-l-4 border-sky-200 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">2. Illustrative Calculations</h3>
                  <p>
                    All calculations, projections, and estimates provided through our calculators are 
                    <span className="font-semibold text-amber-600"> indicative and illustrative only</span>. They are based on assumed rates 
                    of return and should not be considered as guaranteed returns or promises. Actual 
                    returns may vary based on market conditions, policy terms, and other factors.
                  </p>
                </div>

                <div className="border-l-4 border-sky-200 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">3. No Guaranteed Returns</h3>
                  <p>
                    We do <span className="font-semibold text-red-600">NOT</span> promise or guarantee any specific returns on insurance products. 
                    Insurance is subject to market risks and policy terms. Past performance of 
                    any product is not indicative of future results.
                  </p>
                </div>

                <div className="border-l-4 border-sky-200 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">4. Policy Terms Apply</h3>
                  <p>
                    All insurance products are subject to their respective policy terms and conditions, 
                    including but not limited to exclusions, waiting periods, and claim procedures. 
                    Please read the policy documents carefully before purchasing.
                  </p>
                </div>

                <div className="border-l-4 border-sky-200 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">5. Tax Benefits</h3>
                  <p>
                    Tax benefits mentioned on this website are as per prevailing tax laws and are 
                    subject to change. Please consult a tax advisor for specific advice related 
                    to your situation.
                  </p>
                </div>

                <div className="border-l-4 border-sky-200 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">6. Investment Decisions</h3>
                  <p>
                    Any investment decision should be made based on your own assessment of your 
                    financial situation, investment objectives, and risk tolerance. We recommend 
                    consulting with a qualified financial advisor before making any investment decisions.
                  </p>
                </div>
              </div>
            </div>

            {/* Regulatory Compliance */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg p-6 md:p-8 animate-fade-in-up delay-200" data-testid="regulatory-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Regulatory Compliance
                </h2>
              </div>
              
              <div className="space-y-6 text-gray-700">
                <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
                  <div className="flex items-center gap-2 mb-2">
                    <BadgeCheck className="w-5 h-5 text-emerald-500" />
                    <h3 className="font-bold text-gray-900">IRDAI Guidelines</h3>
                  </div>
                  <p>
                    We operate in compliance with the guidelines issued by the Insurance Regulatory 
                    and Development Authority of India (IRDAI). We are committed to ethical business 
                    practices and transparent communication with our clients.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Data Privacy</h3>
                  <p>
                    We are committed to protecting your personal information. The data collected 
                    through this website is used solely for the purpose of providing you with 
                    insurance-related services and communication. We do not sell or share your 
                    personal information with third parties except as required for policy processing.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Grievance Redressal</h3>
                  <p>
                    If you have any complaints or grievances regarding our services, please contact 
                    us at <a href="mailto:Support@futuresanchay.com" className="text-sky-500 hover:underline font-medium">Support@futuresanchay.com</a>. We are committed to resolving your concerns promptly 
                    and fairly.
                  </p>
                </div>
              </div>
            </div>

            {/* Standard Disclaimer */}
            <div className="bg-gray-100 rounded-2xl p-6 text-center animate-fade-in-up delay-300" data-testid="standard-disclaimer">
              <p className="text-sm text-gray-600">
                <span className="font-bold">Standard Disclaimer:</span> "Insurance is the subject matter of solicitation. 
                IRDAI is not involved in activities like selling insurance directly or indirectly. 
                IRDAI does not endorse any advertisements. IRDAI does not vouch for the authenticity of the insurance sold online."
              </p>
            </div>

            {/* Contact for Queries */}
            <div className="text-center animate-fade-in-up delay-400">
              <p className="text-gray-600 mb-4">
                For any queries regarding our compliance practices or to report concerns:
              </p>
              <div className="inline-flex flex-col sm:flex-row gap-4 items-center bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <p className="font-medium text-gray-900">
                  Email: <a href="mailto:Support@futuresanchay.com" className="text-sky-500 hover:underline">Support@futuresanchay.com</a>
                </p>
                <span className="hidden sm:block text-gray-300">|</span>
                <p className="font-medium text-gray-900">
                  Phone: <a href="tel:7410121121" className="text-sky-500 hover:underline">7410121121</a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default CompliancePage;
